import React, { useState, useMemo } from 'react';
import { icons } from './ui/Icons';

/**
 * Componente: DatePicker
 * Seletor de data com navegação entre meses
 */
const DatePicker = ({ selectedDate, onDateSelect, onClose }) => {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const date = selectedDate ? new Date(selectedDate + 'T12:00:00') : new Date();
    return new Date(date.getFullYear(), date.getMonth(), 1);
  });

  // Navegar entre meses
  const goToPreviousMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));
  };

  // Gerar dias do mês
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    // Primeiro dia do mês
    const firstDay = new Date(year, month, 1);
    const firstDayWeek = firstDay.getDay(); // 0 = domingo
    
    // Último dia do mês
    const lastDay = new Date(year, month + 1, 0);
    const totalDays = lastDay.getDate();
    
    // Gerar array com dias
    const days = [];
    
    // Adicionar espaços vazios para alinhar o primeiro dia
    for (let i = 0; i < firstDayWeek; i++) {
      days.push(null);
    }
    
    // Adicionar os dias do mês
    for (let day = 1; day <= totalDays; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dateObj = new Date(year, month, day);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      dateObj.setHours(0, 0, 0, 0);
      
      days.push({
        day,
        dateStr,
        isToday: dateObj.getTime() === today.getTime(),
        isPast: dateObj < today,
        isSelected: dateStr === selectedDate
      });
    }
    
    return days;
  }, [currentMonth, selectedDate]);

  const monthName = currentMonth.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>

      {/* Modal */}
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md z-10 transform transition-all">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900 capitalize">{monthName}</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 rounded-full p-1 hover:bg-gray-100"
            >
              {icons.x({ className: 'h-5 w-5' })}
            </button>
          </div>

          {/* Navegação de Mês */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={goToPreviousMonth}
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 hover:text-gray-900"
            >
              {icons.chevronLeft({ className: 'h-5 w-5' })}
            </button>
            
            <button
              onClick={goToToday}
              className="px-4 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg"
            >
              Hoje
            </button>
            
            <button
              onClick={goToNextMonth}
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 hover:text-gray-900"
            >
              {icons.chevronRight({ className: 'h-5 w-5' })}
            </button>
          </div>

          {/* Dias da Semana */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(day => (
              <div key={day} className="text-center text-xs font-semibold text-gray-500 py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendário */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((dayInfo, index) => {
              if (!dayInfo) {
                return <div key={`empty-${index}`} className="aspect-square"></div>;
              }

              const { day, dateStr, isToday, isPast, isSelected } = dayInfo;

              return (
                <button
                  key={dateStr}
                  onClick={() => {
                    if (!isPast) {
                      onDateSelect(dateStr);
                      onClose();
                    }
                  }}
                  disabled={isPast}
                  className={`
                    aspect-square rounded-lg text-sm font-medium
                    ${isPast ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-indigo-50'}
                    ${isToday && !isSelected ? 'bg-indigo-100 text-indigo-700' : ''}
                    ${isSelected ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'text-gray-700'}
                    ${!isPast && !isSelected && !isToday ? 'hover:bg-gray-100' : ''}
                  `}
                >
                  {day}
                </button>
              );
            })}
          </div>

          {/* Legenda */}
          <div className="mt-4 pt-4 border-t border-gray-200 flex justify-center gap-4 text-xs text-gray-600">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-indigo-600"></div>
              <span>Selecionado</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-indigo-100"></div>
              <span>Hoje</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DatePicker;
